// Jedno wejście do /admin/api. Ciasteczko sesji admina jest httpOnly, więc
// przeglądarka dokłada je sama - my musimy tylko poprosić o wysłanie danych
// uwierzytelniających.

import type { HttpError } from "@refinedev/core";

/**
 * Domyślnie adres względny: na produkcji Caddy przepuszcza /admin/api na to
 * samo API, a lokalnie robi to proxy Vite. Dzięki temu nie ma CORS-u ani
 * ciasteczka na cudzej domenie.
 */
export const API_URL: string = import.meta.env.VITE_ADMIN_API_URL ?? "/admin/api";

interface Problem {
  title?: string;
  detail?: string;
  code?: string;
  errors?: { field: string; message: string }[];
}

/** Błąd w formacie RFC 7807 przetłumaczony na to, czego oczekuje Refine. */
function toHttpError(status: number, problem: Problem): HttpError {
  const message = [problem.title, problem.detail].filter(Boolean).join(" ");
  return {
    message: message || "Coś poszło nie tak. Spróbuj ponownie za chwilę.",
    statusCode: status,
    errors: problem.errors
      ? Object.fromEntries(problem.errors.map((item) => [item.field, item.message]))
      : undefined,
  };
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: URLSearchParams;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const query = options.query?.toString();
  const response = await fetch(`${API_URL}${path}${query ? `?${query}` : ""}`, {
    method: options.method ?? "GET",
    credentials: "include",
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    let problem: Problem = {};
    try {
      problem = (await response.json()) as Problem;
    } catch {
      // Odpowiedź bez treści (np. 502 z proxy) - zostaje sam kod statusu.
    }
    throw toHttpError(response.status, problem);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
